function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const normalized = hex.trim()
    const short = /^#([a-f\d])([a-f\d])([a-f\d])$/i.exec(normalized)
    if (short) {
        return {
            r: Number.parseInt(short[1] + short[1], 16),
            g: Number.parseInt(short[2] + short[2], 16),
            b: Number.parseInt(short[3] + short[3], 16)
        }
    }

    const full = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(normalized)
    if (!full) {
        return null
    }

    return {
        r: Number.parseInt(full[1], 16),
        g: Number.parseInt(full[2], 16),
        b: Number.parseInt(full[3], 16)
    }
}

function rgbToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

function darkenHex(hex: string, amount: number): string {
    const rgb = hexToRgb(hex)
    if (!rgb) {
        return hex
    }

    return rgbToHex(
        Math.floor(rgb.r * (1 - amount)),
        Math.floor(rgb.g * (1 - amount)),
        Math.floor(rgb.b * (1 - amount))
    )
}

/** CSS color channels for `hsl(var(--token))` design tokens (e.g. `175 100% 28%`). */
export function hexToHslChannels(hex: string): string | null {
    const rgb = hexToRgb(hex)
    if (!rgb) {
        return null
    }

    const r = rgb.r / 255
    const g = rgb.g / 255
    const b = rgb.b / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2

    if (max === min) {
        return `0 0% ${Math.round(l * 100)}%`
    }

    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    let h: number
    if (max === r) {
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    } else if (max === g) {
        h = ((b - r) / d + 2) / 6
    } else {
        h = ((r - g) / d + 4) / 6
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/**
 * Remaps app design tokens so global form focus styles (`primary-brand` / `ring`)
 * follow a public therapist theme primary instead of Tebuto teal.
 */
export function publicThemeBrandTokenCss(primaryColor: string): string {
    const brand = hexToHslChannels(primaryColor)
    if (!brand) {
        return ''
    }

    const hover = hexToHslChannels(darkenHex(primaryColor, 0.15)) ?? brand
    const muted = hexToHslChannels(darkenHex(primaryColor, 0.05)) ?? brand

    return `--primary-brand: ${brand};
--primary-hover: ${hover};
--primary-muted: ${muted};
--ring: ${brand};`
}
